import React from 'react';
import { Document, Page, View, Text as PdfText, StyleSheet } from '@react-pdf/renderer';
import { PDF_THEMES, DEFAULT_THEME } from '../themes';
import PdfHeader from '../components/PdfHeader';
import PdfFooter from '../components/PdfFooter';
import PdfSummaryCards from '../components/PdfSummaryCards';
import { PdfTableHead, PdfTableRow, PdfTableFoot } from '../components/PdfTable';
import { fmtINR, fmtDate, cap } from '../pdfUtils';

const s = StyleSheet.create({
  page:         { fontFamily: 'NotoSans', paddingBottom: 60 },
  content:      { marginHorizontal: 40, marginTop: 12 },
  infoBar:      { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8, paddingVertical: 5, paddingHorizontal: 6, borderRadius: 3 },
  sectionHead:  { paddingVertical: 5, paddingHorizontal: 6, marginTop: 12, marginBottom: 4, borderRadius: 3 },
  subLabel:     { fontSize: 8, fontWeight: 'bold', marginTop: 8, marginBottom: 3, paddingLeft: 2 },
});

const PEND_CAT_COLS = [
  { label: 'Category', flex: 2.5 },
  { label: 'Entries',  flex: 0.7, align: 'right' },
  { label: 'Offered',  flex: 1.1, align: 'right' },
  { label: 'Paid',     flex: 1.1, align: 'right' },
  { label: 'Pending',  flex: 1.1, align: 'right' },
];

const PEND_ENTRY_COLS = [
  { label: 'Offering Date', flex: 1 },
  { label: 'Category',      flex: 1.6 },
  { label: 'Offered',       flex: 1,   align: 'right' },
  { label: 'Paid',          flex: 1,   align: 'right' },
  { label: 'Pending',       flex: 1,   align: 'right' },
  { label: 'Status',        flex: 0.9 },
];

const PAID_CAT_COLS = [
  { label: 'Category', flex: 3 },
  { label: 'Entries',  flex: 0.7, align: 'right' },
  { label: 'Offered',  flex: 1.2, align: 'right' },
  { label: 'Paid',     flex: 1.2, align: 'right' },
];

const PAID_ENTRY_COLS = [
  { label: 'Offering Date', flex: 1.1 },
  { label: 'Paid Date',     flex: 1.1 },
  { label: 'Category',      flex: 1.4 },
  { label: 'Offered',       flex: 1,   align: 'right' },
  { label: 'Paid',          flex: 1,   align: 'right' },
  { label: 'Status',        flex: 0.8 },
];

const getCatName  = (o) => o._categoryName || o.DonationCategory?.name || (typeof o.donationCategoryId === 'object' ? o.donationCategoryId?.name : null) || '-';

const getLastPaidDate = (o) => {
  const dates = (o.settlements || []).map((s) => s.Receipt?.receiptDate).filter(Boolean).sort();
  return dates.length > 0 ? dates[dates.length - 1] : null;
};

const groupByCategory = (list) => {
  const map = {};
  list.forEach((o) => {
    const name = getCatName(o);
    if (!map[name]) map[name] = { name, count: 0, offered: 0, paid: 0, pending: 0 };
    map[name].count++;
    map[name].offered  += Number(o.offeredAmount)  || 0;
    map[name].paid     += Number(o.paidAmount)     || 0;
    map[name].pending  += Number(o.pendingAmount)  || 0;
  });
  return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
};

export default function MemberLedgerDocument({ data, theme: themeKey, orientation, sections }) {
  const t = PDF_THEMES[themeKey] || PDF_THEMES[DEFAULT_THEME];

  const pendingRows = Array.isArray(data.pendingOfferings) ? data.pendingOfferings : [];
  const paidRows    = Array.isArray(data.paidOfferings)    ? data.paidOfferings    : [];

  const pendingCats = groupByCategory(pendingRows);
  const paidCats    = groupByCategory(paidRows);

  const totPending = pendingRows.reduce((s, o) => s + (Number(o.pendingAmount) || 0), 0);
  const totPaid    = paidRows.reduce((s, o)    => s + (Number(o.paidAmount)    || 0), 0);

  const SectionHead = ({ label }) => (
    <View style={[s.sectionHead, { backgroundColor: t.primary || t.darkBlue || '#1565C0' }]}>
      <PdfText style={{ fontSize: 9, fontWeight: 'bold', color: '#fff' }}>{label}</PdfText>
    </View>
  );

  const SubLabel = ({ label }) => (
    <View style={s.subLabel}>
      <PdfText style={{ fontSize: 8, fontWeight: 'bold', color: t.body }}>{label}</PdfText>
    </View>
  );

  return (
    <Document>
      <Page size="A4" orientation={orientation || 'landscape'} style={[s.page, { backgroundColor: t.white }]}>
        {sections.header && <PdfHeader derasar={data.derasar} reportTitle="Member Ledger" theme={t} />}
        <View style={s.content}>

          {/* Member name + FY info bar */}
          {(data.memberName || data.fyLabel) && (
            <View style={[s.infoBar, { backgroundColor: t.light }]}>
              <PdfText style={{ fontSize: 9, fontWeight: 'bold', color: t.body }}>
                Member: {data.memberName || '-'}
              </PdfText>
              <PdfText style={{ fontSize: 9, color: t.body }}>
                {data.fyLabel || ''}
              </PdfText>
            </View>
          )}

          {sections.summaryCards && (
            <PdfSummaryCards theme={t} items={[
              { label: 'Pending Entries',       value: String(pendingRows.length), color: t.primary },
              { label: 'Total Pending',         value: fmtINR(totPending),         color: t.statusRed },
              { label: 'Paid Entries (This Year)', value: String(paidRows.length), color: t.statusGreen },
              { label: 'Total Paid (This Year)', value: fmtINR(totPaid),           color: t.statusGreen },
            ]} />
          )}

          {/* ── Pending & Partial section ────────────────────────────────── */}
          <SectionHead label="Pending & Partial — All Financial Years" />

          <SubLabel label="By Category" />
          <PdfTableHead cols={PEND_CAT_COLS} theme={t} />
          {pendingCats.length === 0
            ? <PdfTableRow cols={PEND_CAT_COLS} index={0} theme={t} values={['No pending entries', '', '', '', '']} />
            : pendingCats.map((row, i) => (
                <PdfTableRow key={i} cols={PEND_CAT_COLS} index={i} theme={t} highlight="danger"
                  values={[row.name, String(row.count), fmtINR(row.offered), fmtINR(row.paid), fmtINR(row.pending)]}
                />
              ))
          }
          {pendingCats.length > 0 && (
            <PdfTableFoot cols={PEND_CAT_COLS}
              values={[
                `Total (${pendingCats.length} categories)`,
                String(pendingRows.length),
                fmtINR(pendingCats.reduce((s, c) => s + c.offered, 0)),
                fmtINR(pendingCats.reduce((s, c) => s + c.paid, 0)),
                fmtINR(totPending),
              ]}
              theme={t}
            />
          )}

          <SubLabel label="All Entries" />
          <PdfTableHead cols={PEND_ENTRY_COLS} theme={t} />
          {pendingRows.length === 0
            ? <PdfTableRow cols={PEND_ENTRY_COLS} index={0} theme={t} values={['No pending or partial entries', '', '', '', '', '']} />
            : pendingRows.map((row, i) => (
                <PdfTableRow key={i} cols={PEND_ENTRY_COLS} index={i} theme={t}
                  values={[
                    fmtDate(row.offeringDate),
                    getCatName(row),
                    fmtINR(row.offeredAmount),
                    fmtINR(row.paidAmount),
                    fmtINR(row.pendingAmount),
                    cap(row.status),
                  ]}
                />
              ))
          }
          {pendingRows.length > 0 && (
            <PdfTableFoot cols={PEND_ENTRY_COLS}
              values={[
                `Total (${pendingRows.length} entries)`, '',
                fmtINR(pendingRows.reduce((s, o) => s + (Number(o.offeredAmount) || 0), 0)),
                fmtINR(pendingRows.reduce((s, o) => s + (Number(o.paidAmount)    || 0), 0)),
                fmtINR(totPending),
                '',
              ]}
              theme={t}
            />
          )}

          {/* ── Paid section ─────────────────────────────────────────────── */}
          <SectionHead label="Paid — Current Financial Year" />

          <SubLabel label="By Category" />
          <PdfTableHead cols={PAID_CAT_COLS} theme={t} />
          {paidCats.length === 0
            ? <PdfTableRow cols={PAID_CAT_COLS} index={0} theme={t} values={['No paid entries this year', '', '', '']} />
            : paidCats.map((row, i) => (
                <PdfTableRow key={i} cols={PAID_CAT_COLS} index={i} theme={t}
                  values={[row.name, String(row.count), fmtINR(row.offered), fmtINR(row.paid)]}
                />
              ))
          }
          {paidCats.length > 0 && (
            <PdfTableFoot cols={PAID_CAT_COLS}
              values={[
                `Total (${paidCats.length} categories)`,
                String(paidRows.length),
                fmtINR(paidCats.reduce((s, c) => s + c.offered, 0)),
                fmtINR(totPaid),
              ]}
              theme={t}
            />
          )}

          <SubLabel label="All Entries" />
          <PdfTableHead cols={PAID_ENTRY_COLS} theme={t} />
          {paidRows.length === 0
            ? <PdfTableRow cols={PAID_ENTRY_COLS} index={0} theme={t} values={['No payments recorded this year', '', '', '', '', '']} />
            : paidRows.map((row, i) => (
                <PdfTableRow key={i} cols={PAID_ENTRY_COLS} index={i} theme={t}
                  values={[
                    fmtDate(row.offeringDate),
                    fmtDate(getLastPaidDate(row)) || '-',
                    getCatName(row),
                    fmtINR(row.offeredAmount),
                    fmtINR(row.paidAmount),
                    cap(row.status),
                  ]}
                />
              ))
          }
          {paidRows.length > 0 && (
            <PdfTableFoot cols={PAID_ENTRY_COLS}
              values={[
                `Total (${paidRows.length} entries)`, '', '',
                fmtINR(paidRows.reduce((s, o) => s + (Number(o.offeredAmount) || 0), 0)),
                fmtINR(totPaid),
                '',
              ]}
              theme={t}
            />
          )}

        </View>
        {sections.footer && <PdfFooter theme={t} />}
      </Page>
    </Document>
  );
}
